const DB_NAME = 'myriad_drafts';
const STORE = 'keys';

// Owned here so shared infra (logout, idle timer) doesn't reach into feature
// folders to find the identifiers the crypto + storage layer operate on.
export const DRAFT_KEY_ID = 'self_scheduling_v1';
export const DRAFT_STORAGE_KEY = 'self_scheduling_draft';

const openDb = () =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const idbGet = async (key) => {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
};

const idbPut = async (key, value) => {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
};

const idbDelete = async (key) => {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
};

export const getOrCreateKey = async (keyId) => {
  const existing = await idbGet(keyId);
  if (existing) return existing;
  // extractable=false: an XSS payload can still call encrypt/decrypt with this
  // key, but cannot exfiltrate the raw key bytes via crypto.subtle.exportKey.
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, [
    'encrypt',
    'decrypt'
  ]);
  await idbPut(keyId, key);
  return key;
};

export const clearKey = (keyId) => idbDelete(keyId);

export const clearSelfSchedulingPersistence = async ({ onError } = {}) => {
  try {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch (err) {
    onError?.(err);
  }

  try {
    await clearKey(DRAFT_KEY_ID);
  } catch (err) {
    onError?.(err);
  }
};

export const encrypt = async (plaintextBytes, iv, key) => {
  const buf = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintextBytes);
  return new Uint8Array(buf);
};

export const decrypt = async (ciphertext, iv, key) => {
  const buf = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
  return new Uint8Array(buf);
};

// Chunked to avoid `Maximum call stack size exceeded` from spreading large
// Uint8Arrays into String.fromCharCode (limit is ~65k args in most engines,
// and full wizard drafts can exceed that once card uploads are encoded).
export const b64encode = (bytes) => {
  const view = new Uint8Array(bytes);
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < view.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, view.subarray(i, i + CHUNK));
  }
  return btoa(binary);
};

export const b64decode = (str) => Uint8Array.from(atob(str), (c) => c.charCodeAt(0));
